import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { GateCatalogService } from "../../../../../services/api/gate-catalog/gate-catalog.service";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { NgClass } from "@angular/common";
import { InputNumberModule } from "@openng/optimus-ui/inputnumber";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { Article, ArticleObject } from "../../../../../models/api/gateComponents/article";
import { isNotNullOrUndefined } from "../../../../../shared/utils/utils";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { createTimeoutSignal } from "../../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../../shared/components/timeout/timeout.component";
import { first } from "rxjs";

type ArticleFormField =
  | "enableInspectionLoadChanges"
  | "enableInspectionOperatingTime"
  | "enableExchangeLoadChanges"
  | "enableExchangeOperatingTime"
  | "loadChanges"
  | "operatingTime"
  | "maxLoadChanges"
  | "maxOperatingTime";
@Component({
  standalone: true,
  selector: "app-article",
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
    InputTextModule,
    CheckboxModule,
    NgClass,
    InputNumberModule,
    ProgressSpinnerModule,
    TimeoutComponent,
  ],
  templateUrl: "./article.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./article.component.scss",
})
export class ArticleComponent implements OnInit {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  private gateCatalog = inject(GateCatalogService);

  private article: Article = this.config.data.gateCatalogItem;
  private actionType: "add" | "edit" = this.config.data.actionType;

  public form = new FormGroup({
    label: new FormControl(this.article.label, Validators.required),
    articleNumber: new FormControl(this.article.articleNumber, Validators.required),
    enableInspection: new FormControl(this.article.enableInspection, Validators.required),
    enableInspectionLoadChanges: new FormControl(true),
    loadChanges: new FormControl(this.article.loadChanges, Validators.required),
    enableInspectionOperatingTime: new FormControl(true),
    operatingTime: new FormControl(this.article.operatingTime, Validators.required),
    enableExchange: new FormControl(this.article.enableExchange, Validators.required),
    enableExchangeLoadChanges: new FormControl(true),
    maxLoadChanges: new FormControl(this.article.maxLoadChanges, Validators.required),
    enableExchangeOperatingTime: new FormControl(true),
    maxOperatingTime: new FormControl(this.article.maxOperatingTime, Validators.required),
  });

  public loading = signal(false);
  public timeOut = createTimeoutSignal(this.loading);
  public requestStarted = signal(false);

  constructor() {
    this.form.controls["enableInspection"].valueChanges.pipe(takeUntilDestroyed()).subscribe({
      next: (controlValue) => {
        const enableInspectionLoadChanges = this.form.controls["enableInspectionLoadChanges"].value;
        const enableInspectionOperatingTime = this.form.controls["enableInspectionOperatingTime"].value;
        this.toggleControl("loadChanges", controlValue, enableInspectionLoadChanges);
        this.toggleControl("operatingTime", controlValue, enableInspectionOperatingTime);
      },
    });
    this.form.controls["enableInspectionLoadChanges"].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((controlValue) => {
        const enableInspection = this.form.controls["enableInspection"].value;
        if (!controlValue) {
          this.form.controls["loadChanges"].setValue(null);
        }
        this.toggleControl("loadChanges", controlValue, enableInspection);
      });
    this.form.controls["enableInspectionOperatingTime"].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((controlValue) => {
        const enableInspection = this.form.controls["enableInspection"].value;
        if (!controlValue) {
          this.form.controls["operatingTime"].setValue(null);
        }
        this.toggleControl("operatingTime", controlValue, enableInspection);
      });
    this.form.controls["enableExchange"].valueChanges.pipe(takeUntilDestroyed()).subscribe((controlValue) => {
      const enableExchangeLoadChanges = this.form.controls["enableExchangeLoadChanges"].value;
      const enableExchangeOperatingTime = this.form.controls["enableExchangeOperatingTime"].value;
      this.toggleControl("maxLoadChanges", controlValue, enableExchangeLoadChanges);
      this.toggleControl("maxOperatingTime", controlValue, enableExchangeOperatingTime);
    });
    this.form.controls["enableExchangeLoadChanges"].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((controlValue) => {
        const enableExchange = this.form.controls["enableExchange"].value;
        if (!controlValue) {
          this.form.controls["maxLoadChanges"].setValue(null);
        }

        this.toggleControl("maxLoadChanges", controlValue, enableExchange);
      });
    this.form.controls["enableExchangeOperatingTime"].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((controlValue) => {
        if (!controlValue) {
          this.form.controls["maxOperatingTime"].setValue(null);
        }
        const enableExchange = this.form.controls["enableExchange"].value;
        this.toggleControl("maxOperatingTime", controlValue, enableExchange);
      });
  }

  public ngOnInit() {
    if (this.config.data.gateCatalogItem && this.config.data.actionType) {
      this.loading.set(true);
      if (this.actionType === "edit") {
        this.checkValues();
      }
      this.loading.set(false);
    }
  }

  /**
   * Set the enable load/operating time controls to false if the corresponding value is not set.
   * This is used when the article is being edited and the user has not set the value.
   */
  private checkValues() {
    if (!this.article.loadChanges) {
      this.form.controls["enableInspectionLoadChanges"].setValue(false);
    }
    if (!this.article.operatingTime) {
      this.form.controls["enableInspectionOperatingTime"].setValue(false);
    }
    if (!this.article.maxLoadChanges) {
      this.form.controls["enableExchangeLoadChanges"].setValue(false);
    }
    if (!this.article.maxOperatingTime) {
      this.form.controls["enableExchangeOperatingTime"].setValue(false);
    }
  }

  /**
   * Toggles the control given by controlKey based on the values of
   * operationType and otherDependant.
   *
   * If operationType is true and otherDependant is true, the control is enabled.
   * Otherwise, the control is disabled.
   * @param controlKey the key of the control to toggle
   * @param operationType the value to check for enabling the control
   * @param otherDependant the second value to check for enabling the control
   */
  private toggleControl(
    controlKey: ArticleFormField,
    operationType: boolean | undefined | null,
    otherDependant: boolean | undefined | null
  ) {
    if (isNotNullOrUndefined(operationType) && operationType) {
      if (isNotNullOrUndefined(otherDependant) && otherDependant) {
        this.form.controls[controlKey].enable();
      }
    } else {
      this.form.controls[controlKey].disable();
    }
  }

  /**
   * Extracts the properties that are relevant for the article object.
   * It deletes the properties that are not relevant and returns the object.
   * @returns the extracted article object
   */
  private extractProperties() {
    const articleValues = this.form?.value;
    delete articleValues.enableInspectionLoadChanges;
    delete articleValues.enableInspectionOperatingTime;
    delete articleValues.enableExchangeLoadChanges;
    delete articleValues.enableExchangeOperatingTime;

    if (this.form.get("enableInspection")?.value === false) {
      delete articleValues.loadChanges;
      delete articleValues.operatingTime;
    } else {
      if (this.form.get("enableInspectionLoadChanges")?.value === false) {
        delete articleValues.loadChanges;
      }
      if (this.form.get("enableInspectionOperatingTime")?.value === false) {
        delete articleValues.operatingTime;
      }
    }

    if (this.form.get("enableExchange")?.value === false) {
      delete articleValues.maxLoadChanges;
      delete articleValues.maxOperatingTime;
    } else {
      if (this.form.get("enableExchangeLoadChanges")?.value === false) {
        delete articleValues.maxLoadChanges;
      }
      if (this.form.get("enableExchangeOperatingTime")?.value === false) {
        delete articleValues.maxOperatingTime;
      }
    }
    return articleValues;
  }

  /**
   * Save the article. If the form is valid and the request has not started
   * yet, it will start the request and send the article to the server.
   * If the request is successful, it will close the dialog and return the article.
   * If the request fails, it will show an error message.
   */
  public save() {
    const articleValues = this.extractProperties();

    if (this.form.valid && articleValues) {
      if (!this.requestStarted()) {
        const article = this.getArticleObject();
        this.article.properties = article;
        this.requestStarted.set(true);
        switch (this.actionType) {
          case "add":
            this.gateCatalog
              .createGateCatalogItem(this.article)
              .pipe(first())
              .subscribe({
                next: () => {
                  this.requestStarted.set(false);
                  this.ref.close(this.article);
                },
                error: () => {
                  this.requestStarted.set(false);
                },
              });
            break;
          case "edit":
            this.gateCatalog
              .editGateCatalogItem(this.article)
              .pipe(first())
              .subscribe({
                next: () => {
                  this.requestStarted.set(false);
                  this.ref.close(this.article);
                },
                error: () => {
                  this.requestStarted.set(false);
                },
              });
            break;
        }
      }
    }
  }

  /**
   * Creates an ArticleObject based on the form values.
   * It will return null if the form values are invalid.
   * @returns the ArticleObject or null if the form values are invalid
   */
  private getArticleObject() {
    const articleFormValues = this.form.value;
    if (articleFormValues && articleFormValues.label && isNotNullOrUndefined(articleFormValues.articleNumber)) {
      const article: ArticleObject = {
        id: this.article.id,
        label: articleFormValues.label,
        make: this.article.make,
        articleNumber: articleFormValues.articleNumber,
        enableInspection: !!articleFormValues.enableInspection,
        enableExchange: !!articleFormValues.enableExchange,
        loadChanges: articleFormValues.loadChanges || null,
        operatingTime: articleFormValues.operatingTime || null,
        maxLoadChanges: articleFormValues.maxLoadChanges || null,
        maxOperatingTime: articleFormValues.maxOperatingTime || null,
      };
      return article;
    } else {
      throw new Error("Invalid form values");
    }
  }

  /**
   * Closes the dialog.
   */
  public closeDialog() {
    this.ref.close();
  }
}
